/**
 * SEO Data Registry
 * 
 * Central registry of all site pages with their default SEO values.
 * Custom values are stored in Supabase KV via the content editing system
 * with keys: seo:{pageId}:title, seo:{pageId}:description, seo:{pageId}:slug
 */

import { identity } from "../siteConfig";
import { identity as storeIdentity } from "../storeSiteConfig";

export interface PageSeoEntry {
  /** Internal page ID (used as KV key prefix) */
  id: string;
  /** Human-readable page name */
  name: string;
  /** Default URL path (without leading slash) */
  defaultSlug: string;
  /** Default SEO title */
  defaultTitle: string;
  /** Default meta description */
  defaultDescription: string;
  /** Which template type this page belongs to */
  templateType: "branding" | "store" | "both";
}

/** All static pages in the site */
export const seoPages: PageSeoEntry[] = [
  /* ═══════════════════════════════════════
     Branding (corporate site) pages
     ═══════════════════════════════════════ */
  {
    id: "home",
    name: "\u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA",
    defaultSlug: "",
    defaultTitle: `${identity.nameHebrew} | ${identity.name}`,
    defaultDescription: identity.description,
    templateType: "branding",
  },
  {
    id: "about",
    name: "\u05D0\u05D5\u05D3\u05D5\u05EA",
    defaultSlug: "about",
    defaultTitle: `\u05D0\u05D5\u05D3\u05D5\u05EA | ${identity.nameHebrew}`,
    defaultDescription: `\u05DE\u05D9\u05D3\u05E2 \u05D0\u05D5\u05D3\u05D5\u05EA ${identity.nameHebrew} \u2014 \u05E0\u05D9\u05E1\u05D9\u05D5\u05DF, \u05E2\u05E8\u05DB\u05D9\u05DD \u05D5\u05DE\u05D5\u05DE\u05D7\u05D9\u05D5\u05EA.`,
    templateType: "branding",
  },
  {
    id: "services",
    name: "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD",
    defaultSlug: "services",
    defaultTitle: `\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD | ${identity.nameHebrew}`,
    defaultDescription: `\u05D4\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD \u05D4\u05DE\u05E7\u05E6\u05D5\u05E2\u05D9\u05D9\u05DD \u05E9\u05DC ${identity.nameHebrew} \u2014 \u05E4\u05EA\u05E8\u05D5\u05E0\u05D5\u05EA \u05DE\u05D5\u05EA\u05D0\u05DE\u05D9\u05DD \u05D0\u05D9\u05E9\u05D9\u05EA.`,
    templateType: "branding",
  },
  {
    id: "service-a",
    name: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3",
    defaultSlug: "service-a",
    defaultTitle: `\u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3 | ${identity.nameHebrew}`,
    defaultDescription: `\u05DE\u05D9\u05D3\u05E2 \u05DE\u05E4\u05D5\u05E8\u05D8 \u05E2\u05DC \u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3 \u05E9\u05DC ${identity.nameHebrew}.`,
    templateType: "branding",
  },
  {
    id: "service-b",
    name: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3",
    defaultSlug: "service-b",
    defaultTitle: `\u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3 | ${identity.nameHebrew}`,
    defaultDescription: `\u05DE\u05D9\u05D3\u05E2 \u05DE\u05E4\u05D5\u05E8\u05D8 \u05E2\u05DC \u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3 \u05E9\u05DC ${identity.nameHebrew}.`,
    templateType: "branding",
  },
  {
    id: "products",
    name: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD",
    defaultSlug: "products",
    defaultTitle: `\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD | ${identity.nameHebrew}`,
    defaultDescription: `\u05D4\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05E9\u05DC ${identity.nameHebrew} \u2014 \u05D0\u05D9\u05DB\u05D5\u05EA \u05D5\u05DE\u05E7\u05E6\u05D5\u05E2\u05D9\u05D5\u05EA.`,
    templateType: "branding",
  },
  {
    id: "product-a",
    name: "\u05DE\u05D5\u05E6\u05E8 \u05D0\u05F3",
    defaultSlug: "product-a",
    defaultTitle: `\u05DE\u05D5\u05E6\u05E8 \u05D0\u05F3 | ${identity.nameHebrew}`,
    defaultDescription: `\u05DE\u05D9\u05D3\u05E2 \u05DE\u05E4\u05D5\u05E8\u05D8 \u05E2\u05DC \u05DE\u05D5\u05E6\u05E8 \u05D0\u05F3 \u05E9\u05DC ${identity.nameHebrew}.`,
    templateType: "branding",
  },
  {
    id: "product-b",
    name: "\u05DE\u05D5\u05E6\u05E8 \u05D1\u05F3",
    defaultSlug: "product-b",
    defaultTitle: `\u05DE\u05D5\u05E6\u05E8 \u05D1\u05F3 | ${identity.nameHebrew}`,
    defaultDescription: `\u05DE\u05D9\u05D3\u05E2 \u05DE\u05E4\u05D5\u05E8\u05D8 \u05E2\u05DC \u05DE\u05D5\u05E6\u05E8 \u05D1\u05F3 \u05E9\u05DC ${identity.nameHebrew}.`,
    templateType: "branding",
  },
  {
    id: "projects",
    name: "\u05EA\u05D9\u05E7 \u05E2\u05D1\u05D5\u05D3\u05D5\u05EA",
    defaultSlug: "projects",
    defaultTitle: `\u05EA\u05D9\u05E7 \u05E2\u05D1\u05D5\u05D3\u05D5\u05EA | ${identity.nameHebrew}`,
    defaultDescription: `\u05EA\u05D9\u05E7 \u05D4\u05E2\u05D1\u05D5\u05D3\u05D5\u05EA \u05E9\u05DC ${identity.nameHebrew} \u2014 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD \u05E0\u05D1\u05D7\u05E8\u05D9\u05DD.`,
    templateType: "branding",
  },
  {
    id: "consulting",
    name: "\u05D9\u05D9\u05E2\u05D5\u05E5",
    defaultSlug: "consulting",
    defaultTitle: `\u05D9\u05D9\u05E2\u05D5\u05E5 | ${identity.nameHebrew}`,
    defaultDescription: `\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9 \u05D9\u05D9\u05E2\u05D5\u05E5 \u05DE\u05E7\u05E6\u05D5\u05E2\u05D9 \u05E9\u05DC ${identity.nameHebrew}.`,
    templateType: "branding",
  },
  {
    id: "blog",
    name: "\u05D1\u05DC\u05D5\u05D2",
    defaultSlug: "blog",
    defaultTitle: `\u05D1\u05DC\u05D5\u05D2 | ${identity.nameHebrew}`,
    defaultDescription: `\u05DE\u05D0\u05DE\u05E8\u05D9\u05DD \u05DE\u05E7\u05E6\u05D5\u05E2\u05D9\u05D9\u05DD \u05D5\u05EA\u05D5\u05D1\u05E0\u05D5\u05EA \u05DE${identity.nameHebrew}.`,
    templateType: "branding",
  },
  {
    id: "contact",
    name: "\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8",
    defaultSlug: "contact",
    defaultTitle: `\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8 | ${identity.nameHebrew}`,
    defaultDescription: `\u05E6\u05E8\u05D5 \u05E7\u05E9\u05E8 \u05E2\u05DD ${identity.nameHebrew} \u2014 \u05E0\u05E9\u05DE\u05D7 \u05DC\u05E2\u05D6\u05D5\u05E8.`,
    templateType: "branding",
  },

  /* ═══════════════════════════════════════
     Store (e-commerce / catalog) pages
     ═══════════════════════════════════════ */
  {
    id: "store-home",
    name: "\u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA \u2014 \u05D7\u05E0\u05D5\u05EA",
    defaultSlug: "",
    defaultTitle: `${storeIdentity.nameHebrew} | ${storeIdentity.name}`,
    defaultDescription: storeIdentity.description,
    templateType: "store",
  },
  {
    id: "store-about",
    name: "\u05D0\u05D5\u05D3\u05D5\u05EA \u2014 \u05D7\u05E0\u05D5\u05EA",
    defaultSlug: "about",
    defaultTitle: `\u05D0\u05D5\u05D3\u05D5\u05EA | ${storeIdentity.nameHebrew}`,
    defaultDescription: `\u05DE\u05D9\u05D3\u05E2 \u05D0\u05D5\u05D3\u05D5\u05EA ${storeIdentity.nameHebrew} \u2014 \u05D4\u05E1\u05D9\u05E4\u05D5\u05E8 \u05E9\u05DC\u05E0\u05D5, \u05D4\u05D7\u05D6\u05D5\u05DF \u05D5\u05D4\u05DE\u05D5\u05DE\u05D7\u05D9\u05D5\u05EA.`,
    templateType: "store",
  },
  {
    id: "store-catalog",
    name: "\u05E7\u05D8\u05DC\u05D5\u05D2 \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD",
    defaultSlug: "catalog",
    defaultTitle: `\u05E7\u05D8\u05DC\u05D5\u05D2 \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD | ${storeIdentity.nameHebrew}`,
    defaultDescription: `\u05E2\u05D9\u05D9\u05E0\u05D5 \u05D1\u05E7\u05D8\u05DC\u05D5\u05D2 \u05D4\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05D4\u05DE\u05DC\u05D0 \u05E9\u05DC ${storeIdentity.nameHebrew} \u2014 \u05DE\u05D2\u05D5\u05D5\u05DF \u05E8\u05D7\u05D1 \u05D1\u05DE\u05D7\u05D9\u05E8\u05D9\u05DD \u05DE\u05E9\u05EA\u05DC\u05DE\u05D9\u05DD.`,
    templateType: "store",
  },
  {
    id: "store-category",
    name: "\u05E2\u05DE\u05D5\u05D3 \u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4",
    defaultSlug: "category/:slug",
    defaultTitle: `\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4 | ${storeIdentity.nameHebrew}`,
    defaultDescription: `\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05D1\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4 \u05D4\u05E0\u05D1\u05D7\u05E8\u05EA \u2014 ${storeIdentity.nameHebrew}.`,
    templateType: "store",
  },
  {
    id: "store-product",
    name: "\u05E2\u05DE\u05D5\u05D3 \u05DE\u05D5\u05E6\u05E8",
    defaultSlug: "product/:slug",
    defaultTitle: `\u05DE\u05D5\u05E6\u05E8 | ${storeIdentity.nameHebrew}`,
    defaultDescription: `\u05E4\u05E8\u05D8\u05D9 \u05DE\u05D5\u05E6\u05E8, \u05DE\u05E4\u05E8\u05D8 \u05D5\u05DE\u05D7\u05D9\u05E8 \u2014 ${storeIdentity.nameHebrew}.`,
    templateType: "store",
  },
  {
    id: "store-contact",
    name: "\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8 \u2014 \u05D7\u05E0\u05D5\u05EA",
    defaultSlug: "contact",
    defaultTitle: `\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8 | ${storeIdentity.nameHebrew}`,
    defaultDescription: `\u05E6\u05E8\u05D5 \u05E7\u05E9\u05E8 \u05E2\u05DD ${storeIdentity.nameHebrew} \u2014 \u05E0\u05E9\u05DE\u05D7 \u05DC\u05E2\u05D6\u05D5\u05E8.`,
    templateType: "store",
  },
];

/** Helper: get SEO content key */
export function seoKey(pageId: string, field: "title" | "description" | "slug"): string {
  return `seo:${pageId}:${field}`;
}

/** Get pages filtered by template type */
export function getSeoPagesForType(type: "branding" | "store"): PageSeoEntry[] {
  return seoPages.filter((p) => p.templateType === type || p.templateType === "both");
}

/** Map from default slug to page ID for quick lookup */
export const slugToPageId: Record<string, string> = {};
for (const page of seoPages) {
  if (page.defaultSlug) {
    slugToPageId[page.defaultSlug] = page.id;
  }
}

/** Map from page ID to page entry */
export const pageById: Record<string, PageSeoEntry> = {};
for (const page of seoPages) {
  pageById[page.id] = page;
}
